import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Reply, ThumbsUp, Heart, Smile, ChevronDown, ChevronRight, Trash2, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../services/AuthContext';
import { formatSecondsToTime } from '../utils/timestampParser';
import { useAudio } from '../contexts/AudioContext';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:7777' : '';

interface Comment {
  id: string;
  userEmail?: string;
  userName?: string;
  content: string;
  timestampSeconds?: number;
  parentCommentId?: string;
  replies?: Comment[];
  reactions?: CommentReaction[];
  timestamp?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

interface CommentReaction {
  emoji: string;
  count: number;
  userReacted: boolean;
  users?: string;
}

interface CommentPopupProps {
  comments: Comment[];
  position: { x: number; y: number };
  onClose: () => void;
  onReply?: (parentCommentId: string, content: string) => Promise<void>;
  onReact?: (commentId: string, emoji: string) => Promise<void>;
  onDelete?: (commentId: string) => Promise<void>;
  onAddComment?: (content: string, timestampSeconds?: number) => Promise<void>;
  sessionId?: string;
  token?: string;
  timestampSeconds?: number;
  readOnly?: boolean;
}

const CommentPopup: React.FC<CommentPopupProps> = ({
  comments,
  position,
  onClose,
  onReply,
  onReact,
  onDelete,
  onAddComment,
  sessionId,
  token,
  timestampSeconds,
  readOnly = false,
}) => {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuth();
  const audioContext = useAudio();
  const seekToRef = useRef(audioContext.seekTo);
  
  // Update ref when seekTo changes
  useEffect(() => {
    seekToRef.current = audioContext.seekTo;
  }, [audioContext.seekTo]);
  const popupRef = useRef<HTMLDivElement>(null);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [reactions, setReactions] = useState<Record<string, CommentReaction[]>>({});
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState('');
  const canReply = !readOnly && !!onReply;
  const canReact = !readOnly && !!onReact;
  const canAddComment = !readOnly && !!onAddComment;

  // Load reactions
  useEffect(() => {
    const loadReactions = async () => {
      if (!sessionId || !token) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/reactions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setReactions(data.reactions || {});
        }
      } catch (error) {
        console.error('Failed to load reactions:', error);
      }
    };
    void loadReactions();
  }, [sessionId, token]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const toggleThread = (commentId: string) => {
    setExpandedThreads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const handleTimestampClick = (seconds: number) => {
    if (seekToRef.current) {
      seekToRef.current(seconds);
    }
  };

  const handleReplySubmit = async (parentId: string) => {
    if (!replyContent.trim() || !onReply) return;
    await onReply(parentId, replyContent);
    setReplyContent('');
    setReplyingTo(null);
  };

  const canDeleteComment = (comment: Comment) => {
    if (!user) return false;
    return comment.userEmail === user.email;
  };

  const getUserInitials = (email: string) => {
    if (email === 'Anonymous') return 'AN';
    return email.split('@')[0].substring(0, 2).toUpperCase();
  };

  const getUserColor = (email: string) => {
    const colors = [
      'bg-[#232E3F]', 'bg-[#435065]', 'bg-[#8B5B00]', 'bg-[#B56A5D]',
      'bg-[#FFB52B]', 'bg-[#5C6675]', 'bg-[#6B5130]', 'bg-[#A48757]'
    ];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const formatTimestamp = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const CommentThread: React.FC<{ comment: Comment; depth: number }> = ({ comment, depth }) => {
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isExpanded = expandedThreads.has(comment.id);
    const userEmail = comment.userEmail || 'Anonymous';
    const userName = comment.userName || 'Anonymous';
    const displayTimestamp = comment.timestamp || comment.createdAt || comment.updatedAt;
    const commentReactions = reactions[comment.id] || comment.reactions || [];

    return (
      <div className={`${depth > 0 ? 'ml-6 mt-2' : ''}`}>
        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-3`}>
          <div className="flex items-start space-x-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white ${getUserColor(userEmail)}`}>
              {userEmail.includes('@') && userEmail !== 'Anonymous' ? (
                getUserInitials(userEmail)
              ) : (
                <User size={14} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium truncate">
                    {userName}
                  </span>
                  {comment.timestampSeconds !== null && comment.timestampSeconds !== undefined && (
                    <button
                      onClick={() => handleTimestampClick(comment.timestampSeconds!)}
                      className="font-mono text-xs text-[#B57700] hover:text-[#8B5B00]"
                    >
                      {formatSecondsToTime(comment.timestampSeconds)}
                    </button>
                  )}
                  {displayTimestamp && (
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(displayTimestamp)}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  {canReply && (
                    <button
                      onClick={() => setReplyingTo(comment.id)}
                      className={`p-1 rounded transition-colors ${
                        isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                      }`}
                      title="Reply"
                    >
                      <Reply size={14} />
                    </button>
                  )}
                  {canDeleteComment(comment) && onDelete && (
                    <button
                      onClick={() => onDelete(comment.id)}
                      className={`text-gray-400 hover:text-red-500 transition-colors p-1 rounded`}
                      title="Delete comment"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              
              <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} prose prose-sm ${isDarkMode ? 'prose-invert' : ''} max-w-none`}>
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => <p className={`mb-2 last:mb-0 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</p>,
                    ul: ({ children }) => <ul className={`list-disc list-inside mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</ul>,
                    ol: ({ children }) => <ol className={`list-decimal list-inside mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</ol>,
                    li: ({ children }) => <li className={`mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</li>,
                    h1: ({ children }) => <h1 className={`text-base font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h1>,
                    h2: ({ children }) => <h2 className={`text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h2>,
                    h3: ({ children }) => <h3 className={`text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</h3>,
                    strong: ({ children }) => <strong className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{children}</strong>,
                    em: ({ children }) => <em className={`italic ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</em>,
                    blockquote: ({ children }) => <blockquote className={`border-l-4 ${isDarkMode ? 'border-gray-500 text-gray-300' : 'border-gray-300 text-gray-700'} pl-4 italic mb-2`}>{children}</blockquote>,
                    code: ({ className, children, ...props }: any) => {
                      const isInline = !className;
                      const codeClasses = isInline 
                        ? `${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'} px-1 py-0.5 rounded text-xs`
                        : `block ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'} p-2 rounded text-xs overflow-x-auto`;
                      return (
                        <code className={codeClasses} {...props}>
                          {children}
                        </code>
                      );
                    },
                    a: ({ children, href }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#B57700] hover:text-[#8B5B00] underline">
                        {children}
                      </a>
                    )
                  }}
                >
                  {comment.content}
                </ReactMarkdown>
              </div>
              
              {/* Reactions */}
              {(commentReactions.length > 0 || canReact) && (
                <div className="flex items-center gap-2 mt-2">
                {commentReactions.map((reaction, idx) => {
                  const userList = reaction.users?.split(',').map(email => {
                    const name = email.split('@')[0];
                    return name.charAt(0).toUpperCase() + name.slice(1);
                  }).join(', ');
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        if (onReact) {
                          void onReact(comment.id, reaction.emoji);
                        }
                      }}
                      disabled={!canReact}
                      className={`relative group flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                        reaction.userReacted
                          ? isDarkMode 
                            ? 'border border-[#445166] bg-[#232E3F] text-[#F4F4F4] hover:bg-[#2F3B4E]'
                            : 'border border-[#E7B14C]/45 bg-[#FFF4D8] text-[#8B5B00] hover:bg-[#FFE7AD]'
                          : isDarkMode
                            ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      title={userList || 'No users'}
                    >
                      <span>{reaction.emoji}</span>
                      <span>{reaction.count}</span>
                    </button>
                  );
                })}
                
                {/* Add reaction buttons */}
                {canReact && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        if (onReact) {
                          void onReact(comment.id, '👍');
                        }
                      }}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200'
                          : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      onClick={() => {
                        if (onReact) {
                          void onReact(comment.id, '❤️');
                        }
                      }}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200'
                          : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Heart size={14} />
                    </button>
                    <button
                      onClick={() => {
                        if (onReact) {
                          void onReact(comment.id, '😊');
                        }
                      }}
                      className={`p-1.5 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-600 text-gray-400 hover:text-gray-200'
                          : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Smile size={14} />
                    </button>
                  </div>
                )}
              </div>
              )}
              
              {/* Reply form */}
              {canReply && replyingTo === comment.id && (
                <div className="mt-2 flex items-end gap-2">
                  <input
                    type="text"
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleReplySubmit(comment.id);
                      }
                    }}
                    placeholder="Write a reply..."
                    className={`flex-1 px-3 py-1 text-sm rounded-lg border ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-600 text-gray-200'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/35`}
                    autoFocus
                  />
                  <button
                    onClick={() => handleReplySubmit(comment.id)}
                    className="rounded-lg bg-[#FFB52B] p-1.5 text-[#232E3F] transition-colors hover:bg-[#FFC54D]"
                  >
                    <Send size={14} />
                  </button>
                </div>
              )}
              
              {/* Thread toggle */}
              {hasReplies && (
                <button
                  onClick={() => toggleThread(comment.id)}
                  className={`flex items-center gap-1 mt-2 text-xs ${
                    isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span>{comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Render replies */}
        {hasReplies && isExpanded && (
          <div className="mt-2">
            {comment.replies!.map((reply) => (
              <CommentThread key={reply.id} comment={reply} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Calculate popup position to stay within viewport
  const viewportPadding = 12;
  const popupWidth = Math.min(384, window.innerWidth - (viewportPadding * 2));
  const popupHeight = Math.min(500, window.innerHeight - (viewportPadding * 2));
  const popupStyle = {
    width: `${popupWidth}px`,
    maxHeight: `${popupHeight}px`,
    left: Math.max(
      viewportPadding,
      Math.min(position.x, window.innerWidth - popupWidth - viewportPadding),
    ),
    top: Math.max(
      viewportPadding,
      Math.min(position.y, window.innerHeight - popupHeight - viewportPadding),
    ),
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={popupRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`fixed z-50 overflow-y-auto rounded-lg shadow-2xl ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
        style={popupStyle}
      >
        <div className="sticky top-0 flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-inherit">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-[#B57700] dark:text-[#FFCF74]" />
            <span className="font-medium">Comments</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        {readOnly && (
          <div className={`border-b px-3 py-2 text-xs ${
            isDarkMode ? 'border-gray-700 bg-gray-900/60 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500'
          }`}>
            Offline and public-share comments are read-only in this view.
          </div>
        )}
        
        <div className="p-3 space-y-3">
          {comments.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-500 mb-4">No comments yet</p>
              {canAddComment && (
                <button
                  onClick={() => setIsAddingComment(true)}
                  className="rounded-lg bg-[#FFB52B] px-4 py-2 text-sm text-[#232E3F] transition-colors hover:bg-[#FFC54D]"
                >
                  Add the first comment
                </button>
              )}
            </div>
          ) : (
            <>
              {comments.map(comment => (
                <CommentThread key={comment.id} comment={comment} depth={0} />
              ))}
              {canAddComment && (
                <button
                  onClick={() => setIsAddingComment(true)}
                  className={`w-full px-3 py-2 text-sm rounded-lg transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Add a comment
                </button>
              )}
            </>
          )}
          
          {/* New comment input */}
          {isAddingComment && canAddComment && onAddComment && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex items-end gap-2">
                <input
                  type="text"
                  value={newCommentContent}
                  onChange={(e) => setNewCommentContent(e.target.value)}
                  onKeyPress={async (e) => {
                    if (e.key === 'Enter' && newCommentContent.trim()) {
                      await onAddComment(newCommentContent, timestampSeconds);
                      setNewCommentContent('');
                      setIsAddingComment(false);
                    }
                  }}
                  placeholder="Write a comment..."
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-600 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/35`}
                  autoFocus
                />
                <button
                  onClick={async () => {
                    if (newCommentContent.trim()) {
                      await onAddComment(newCommentContent, timestampSeconds);
                      setNewCommentContent('');
                      setIsAddingComment(false);
                    }
                  }}
                  className="rounded-lg bg-[#FFB52B] p-2 text-[#232E3F] transition-colors hover:bg-[#FFC54D]"
                >
                  <Send size={16} />
                </button>
                <button
                  onClick={() => {
                    setIsAddingComment(false);
                    setNewCommentContent('');
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommentPopup;
